import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingBag,
    Globe,
    Code,
    Layout,
    Monitor,
    Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { SEO } from '@/components/landing/shared/SEO';
import { toast } from 'sonner';
import { IntegrationGuide } from '@/components/integrations/IntegrationGuide';
import { AiImplementationList } from '@/components/integrations/AiImplementationList';

interface IntegrationsPageProps {
    isDashboard?: boolean;
}

const PLATFORMS = [
    {
        id: 'shopify',
        name: 'Shopify',
        icon: ShoppingBag,
        color: 'text-[#95BF47]',
        bg: 'bg-[#95BF47]/10'
    },
    {
        id: 'wordpress',
        name: 'WordPress',
        icon: Globe,
        color: 'text-[#21759B]',
        bg: 'bg-[#21759B]/10'
    },
    {
        id: 'wix',
        name: 'Wix',
        icon: Layout,
        color: 'text-[#0C6EFC]',
        bg: 'bg-[#0C6EFC]/10'
    },
    {
        id: 'squarespace',
        name: 'Squarespace',
        icon: Monitor,
        color: 'text-[#000000] dark:text-white',
        bg: 'bg-[#000000]/10 dark:bg-white/10'
    },
    {
        id: 'custom',
        name: 'Developer',
        icon: Code,
        color: 'text-primary',
        bg: 'bg-primary/10'
    },
    {
        id: 'ai',
        name: 'Ask AI',
        icon: Sparkles,
        color: 'text-amber-500',
        bg: 'bg-amber-500/10'
    }
];

const IntegrationsPage = ({ isDashboard = false }: IntegrationsPageProps) => {
    const location = useLocation();
    const queryPlatform = new URLSearchParams(location.search).get('p');
    const [selectedPlatform, setSelectedPlatform] = useState(queryPlatform && PLATFORMS.some(p => p.id === queryPlatform) ? queryPlatform : 'shopify');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const p = new URLSearchParams(location.search).get('p');
        if (p && PLATFORMS.some(platform => platform.id === p)) {
            setSelectedPlatform(p);
        }
    }, [location.search]);

    const snippet = `<script 
  src="https://chytbot.com/loader.js" 
  data-bot-id="YOUR_BOT_ID" 
  defer
></script>`;

    const copySnippet = () => {
        navigator.clipboard.writeText(snippet);
        setCopied(true);
        toast.success('Snippet copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    // Platform-specific SEO keywords
    const platformKeywords: Record<string, string[]> = {
        shopify: ['Shopify chatbot', 'Shopify AI assistant', 'Shopify customer support bot', 'add chatbot to Shopify store'],
        wordpress: ['WordPress chatbot plugin', 'WordPress AI chat', 'add chatbot to WordPress', 'WordPress customer service bot'],
        wix: ['Wix chatbot', 'Wix AI assistant', 'add chatbot to Wix', 'Wix customer support'],
        squarespace: ['Squarespace chatbot', 'Squarespace AI assistant', 'add chatbot to Squarespace', 'Squarespace customer support'],
        custom: ['React chatbot', 'Next.js chatbot', 'Vue chatbot', 'JavaScript chatbot integration']
    };

    return (
        <div className={cn(
            "relative min-h-screen pb-12 sm:pb-20 bg-background",
            isDashboard ? "pt-6 sm:pt-8" : "pt-24 sm:pt-28 lg:pt-32"
        )}>
            <SEO
                title="Put an AI Chatbot Online - Shopify, WordPress and More"
                description="Step-by-step guides to put a live AI chatbot online on Shopify, WordPress, Wix, or Squarespace in under 5 minutes. Website chatbot setup with no coding required."
                url="https://chytbot.com/integrations"
                keywords={[
                    "AI chatbot integration",
                    "website chatbot",
                    "website chatbot setup",
                    "chatbot online",
                    "AI chatbot online",
                    "chatbot live",
                    "AI chat online",
                    "ecommerce chatbot",
                    "customer support automation",
                    "chatbot installation guide",
                    "add chatbot to website",
                    "ChytBot setup",
                    "no-code chatbot",
                    ...platformKeywords[selectedPlatform] || []
                ]}
                structuredData={[
                    {
                        "@context": "https://schema.org",
                        "@type": "HowTo",
                        "name": "How to Add ChytBot AI Chatbot to Your Website",
                        "description": "Complete guide to integrating ChytBot with Shopify, WordPress, Wix, Squarespace, and custom websites",
                        "image": "https://chytbot.com/og-image.png",
                        "totalTime": "PT5M",
                        "estimatedCost": {
                            "@type": "MonetaryAmount",
                            "currency": "USD",
                            "value": "0"
                        },
                        "step": [
                            {
                                "@type": "HowToStep",
                                "position": 1,
                                "name": "Sign in to ChytBot",
                                "text": "Log in to your ChytBot dashboard at chytbot.com"
                            },
                            {
                                "@type": "HowToStep",
                                "position": 2,
                                "name": "Copy your embed code",
                                "text": "Navigate to 'Add to Website' and copy your unique bot snippet"
                            },
                            {
                                "@type": "HowToStep",
                                "position": 3,
                                "name": "Access your website editor",
                                "text": "Open your platform's theme editor or code injection settings"
                            },
                            {
                                "@type": "HowToStep",
                                "position": 4,
                                "name": "Paste the code",
                                "text": "Add the ChytBot snippet in the header section of your website"
                            },
                            {
                                "@type": "HowToStep",
                                "position": 5,
                                "name": "Save and verify",
                                "text": "Save your changes and visit your website to see ChytBot in action"
                            }
                        ],
                        "tool": [
                            {
                                "@type": "HowToTool",
                                "name": "ChytBot account"
                            }
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How long does it take to add ChytBot to my website?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Most integrations take less than 5 minutes. Simply copy your embed code and paste it into your website's header section."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Do I need coding skills to integrate ChytBot?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "No coding skills required! Our step-by-step guides walk you through the entire process for Shopify, WordPress, Wix, and Squarespace."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Is ChytBot compatible with my website platform?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "ChytBot works with any website that supports custom scripts, including Shopify, WordPress, Wix, Squarespace, and custom-built sites."
                                }
                            }
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://chytbot.com"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Integrations",
                                "item": "https://chytbot.com/integrations"
                            }
                        ]
                    }
                ]}
            />

            {/* Background gradient */}
            <div className="absolute top-0 left-0 w-full h-80 bg-gradient-to-b from-primary/5 via-background to-transparent pointer-events-none -z-10" />

            <div className={cn(
                "w-full mx-auto",
                isDashboard ? "max-w-7xl px-4 sm:px-6 lg:px-8" : "container px-4 sm:px-6"
            )}>
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-12 lg:mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-3 sm:mb-4 text-foreground">
                            <span className="text-primary">Integrations</span>
                        </h1>
                        <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Add ChytBot to your website in minutes. Select your platform below for step-by-step instructions.
                        </p>
                    </motion.div>
                </div>

                {/* Main Content Card */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="max-w-6xl mx-auto border border-border rounded-xl sm:rounded-2xl bg-card shadow-sm overflow-hidden"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr]">
                        {/* Mobile: horizontal scrollable platform strip */}
                        <div className="lg:hidden flex overflow-x-auto gap-2 p-3 sm:p-4 border-b border-border bg-muted/10 scrollbar-hide">
                            {PLATFORMS.map((platform) => (
                                <button
                                    key={platform.id}
                                    onClick={() => setSelectedPlatform(platform.id)}
                                    className={cn(
                                        "flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all duration-200 font-semibold text-xs sm:text-sm min-h-[44px]",
                                        selectedPlatform === platform.id
                                            ? "bg-background shadow-sm ring-1 ring-primary/20 text-primary"
                                            : "text-muted-foreground hover:bg-muted/50"
                                    )}
                                >
                                    <platform.icon className={cn("w-4 h-4", selectedPlatform === platform.id ? "text-primary" : "opacity-60")} />
                                    {platform.name}
                                </button>
                            ))}
                        </div>

                        {/* Desktop: sidebar platform selector */}
                        <div className="hidden lg:flex flex-col border-r border-border bg-muted/10 p-4 space-y-1">
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-3 px-3 pt-1">Platforms</p>
                            {PLATFORMS.map((platform) => (
                                <button
                                    key={platform.id}
                                    onClick={() => setSelectedPlatform(platform.id)}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm min-h-[44px]",
                                        selectedPlatform === platform.id
                                            ? "bg-background shadow-sm ring-1 ring-primary/20 text-primary"
                                            : "text-muted-foreground hover:bg-muted/50"
                                    )}
                                >
                                    <platform.icon className={cn("w-4 h-4", selectedPlatform === platform.id ? "text-primary" : "opacity-60")} />
                                    {platform.name}
                                </button>
                            ))}
                        </div>

                        {/* Guide Content */}
                        <div className="p-4 sm:p-6 lg:p-8">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={selectedPlatform}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {selectedPlatform === 'ai' ? (
                                        <div className="space-y-6">
                                            <div>
                                                <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2 sm:mb-3">Ask AI to Implement</h2>
                                                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                                                    Don't want to code? Copy one of these prompts and paste it into your AI coding assistant.
                                                </p>
                                            </div>
                                            <AiImplementationList isDashboard={isDashboard} />
                                        </div>
                                    ) : (
                                        <IntegrationGuide platformId={selectedPlatform} snippet={snippet} copySnippet={copySnippet} copied={copied} isDashboard={isDashboard} />
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>

                {/* Internal Backlinks Section */}
                <div className="mt-10 sm:mt-14 lg:mt-16 max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Link to="/#features" className="group p-4 sm:p-5 rounded-xl border border-border bg-card hover:border-primary/20 hover:shadow-md transition-all">
                            <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-1">Explore Features</h4>
                            <p className="text-sm text-muted-foreground">Discover all AI capabilities ChytBot offers</p>
                        </Link>
                        <Link to="/pricing" className="group p-4 sm:p-5 rounded-xl border border-border bg-card hover:border-primary/20 hover:shadow-md transition-all">
                            <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-1">View Pricing</h4>
                            <p className="text-sm text-muted-foreground">Plans starting at $9.99/month</p>
                        </Link>
                        <Link to="/auth" className="group p-4 sm:p-5 rounded-xl border border-border bg-card hover:border-primary/20 hover:shadow-md transition-all sm:col-span-2 lg:col-span-1">
                            <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-1">Get Started Free</h4>
                            <p className="text-sm text-muted-foreground">Create your first chatbot in minutes</p>
                        </Link>
                    </div>
                </div>

                {/* Contact Section */}
                <div className="mt-10 sm:mt-12 lg:mt-14 py-8 sm:py-10 border-t border-border text-center">
                    <h3 className="text-base sm:text-lg font-bold mb-2">Need Custom Integration?</h3>
                    <p className="text-muted-foreground mb-5 sm:mb-6 text-sm max-w-lg mx-auto">
                        Our team can assist with white-label solutions, API integrations, or enterprise deployments.
                    </p>
                    <Button variant="outline" className="rounded-xl h-11 min-h-[44px] px-6 font-semibold border-primary/20 hover:bg-primary/5 text-primary transition-all" asChild>
                        <Link to="/contact?inquiry=sales">Contact Sales</Link>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default IntegrationsPage;
